import React, { useContext, useMemo } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { SelectField, UniformCard, Select } from '@ubeya/shared-web/components';
import useAddNewSelector from '@ubeya/shared-web/hooks/useAddNewSelector';
import useUniforms from '@ubeya/shared/hooks/account/useUniforms';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { useField } from 'react-final-form';
import UniformModal from 'settings/components/BranchSettings/Uniforms/UniformModal';

const Wrapper = styled.div.attrs(() => ({ className: 'uniform-wrapper' }))`
  ${({ $hideBorder }) =>
    $hideBorder &&
    css`
      .select {
        border: none;
        box-shadow: none;
      }
    `}
`;

const UniformSelectField = React.forwardRef(
  (
    {
      onAdd,
      onChangeValue,
      value,
      onChange,
      hideBorder = false,
      hideTitle = false,
      name = 'uniformId',
      showFullSelectedUniform = true,
      useMinWidthAsMaxWidth = true,
      enableAdd = true,
      optionsFullWidth = false,
      ...props
    },
    ref
  ) => {
    const { t } = useContext(ConfigContext);
    const { colors } = useTheme();

    const { uniformOptions: defaultUniformOptions, addUniform, mappedUniforms } = useUniforms();
    const {
      isOpen: uniformModal,
      setIsOpen: setUniformModal,
      options: uniformOptions,
      onChange: onChangeUniform
    } = useAddNewSelector({
      options: defaultUniformOptions,
      addText: 'addNewUniform',
      onChange: onChangeValue,
      enable: enableAdd
    });

    const {
      input: { value: uniformId }
    } = useField(name);

    const commonSelectProps = useMemo(
      () => ({
        ref,
        title: t('uniform'),
        useMinWidthAsMaxWidth,
        disableOptionTextWithTooltip: true,
        optionsFullWidth,
        getOptionSpecificStyle: () => ({ borderBottom: `1px solid ${colors.grey2}` }),
        optionStyle: { enableWrap: true },
        options: uniformOptions,
        isMulti: false,
        hideTitle,
        usePortal: true,
        clearable: true,
        creatable: true,
        onBlur: (e) => e.target.value && setUniformModal(e.target.value),
        formatOption: ({ value: valueProp, label, image, description }) => ({
          value: valueProp,
          label: valueProp ? <UniformCard label={label} image={image} description={description} /> : label
        }),
        ...props
      }),
      [ref, t, useMinWidthAsMaxWidth, optionsFullWidth, uniformOptions, hideTitle, props, colors.grey2, setUniformModal]
    );

    return (
      <Wrapper $hideBorder={hideBorder} className={props.className}>
        {onChange ? (
          <Select {...commonSelectProps} value={value} onChange={onChange} />
        ) : (
          <SelectField {...commonSelectProps} name={name} onChange={onChangeUniform} />
        )}

        {showFullSelectedUniform && !!uniformId && (
          <UniformCard
            label={mappedUniforms[uniformId]?.name}
            image={mappedUniforms[uniformId]?.image}
            description={mappedUniforms[uniformId]?.description}
            wrapperStyle={{ background: colors.grey1, borderRadius: 4, padding: 8, margin: '10px 0' }}
          />
        )}

        {uniformModal && (
          <UniformModal
            selectedUniform={typeof uniformModal === 'string' ? { name: uniformModal } : {}}
            onClose={() => setUniformModal(false)}
            onConfirm={async (values) => {
              const { data } = await addUniform(values);
              onAdd(data?.id);
            }}
          />
        )}
      </Wrapper>
    );
  }
);

export default UniformSelectField;
